import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';

/**
 * useLocationSharing — periodically POSTs current location to /api/staff/location.
 *
 * Phase 47.42 — GPS is ALWAYS ON for trackable roles (sales, installation,
 * care, vendor). Removed the localStorage opt-in toggle so admins can
 * always trust that staff on the clock are being live-tracked. The hook
 * silently no-ops when the browser denies permission or geolocation is
 * unsupported (older devices) — no toast noise, just a `permission`
 * status in the return payload for a discreet UI badge.
 *
 * Interval defaults to 60 seconds.
 *
 * Returns:
 *   { enabled, setEnabled, lastReport, lastError, supported, permission }
 *   (`enabled` is now always `true` for trackable roles; `setEnabled`
 *    is retained as a no-op for callers that still reference it.)
 */
const TRACKABLE_ROLES = new Set(['sales', 'installation', 'care', 'vendor']);

export function useLocationSharing(user, intervalMs = 60000) {
    const canTrack = user && TRACKABLE_ROLES.has(user.role);
    // Phase 47.42 — always-on for trackable roles.
    const enabled = !!canTrack;
    const setEnabled = () => {};  // no-op retained for backward compat
    const [lastReport, setLastReport] = useState(null);
    const [lastError, setLastError] = useState(null);
    const [permission, setPermission] = useState('unknown');
    const timerRef = useRef(null);

    const supported = typeof navigator !== 'undefined' && !!navigator.geolocation;

    const reportOnce = async () => {
        if (!supported || !canTrack) return;
        return new Promise((resolve) => {
            navigator.geolocation.getCurrentPosition(
                async (pos) => {
                    try {
                        // Phase 47.42 — Reject coarse (IP-based) fixes so we
                        // never store a stale city-scale point as the staff's
                        // "live" location. > 500 m accuracy is almost always
                        // WiFi/IP triangulation, not GPS. Keep asking for a
                        // finer fix silently — the watchPosition below will
                        // upgrade the reading as soon as GPS locks on.
                        const acc = pos.coords.accuracy || 9999;
                        if (acc > 500) {
                            setLastError(`Waiting for precise GPS fix (± ${Math.round(acc)} m)`);
                            resolve();
                            return;
                        }
                        const battery = await getBatteryLevel();
                        const res = await api.reportStaffLocation({
                            latitude: pos.coords.latitude,
                            longitude: pos.coords.longitude,
                            accuracy: acc,
                            heading: pos.coords.heading,
                            speed: pos.coords.speed,
                            battery,
                            source: 'live',
                        });
                        setLastReport(res.updated_at || new Date().toISOString());
                        setLastError(null);
                        setPermission('granted');
                    } catch (e) {
                        setLastError(e?.response?.data?.detail || e?.message || 'Failed');
                    }
                    resolve();
                },
                (err) => {
                    setLastError(err?.message || 'Geolocation error');
                    setPermission(err?.code === 1 ? 'denied' : 'error');
                    resolve();
                },
                // Phase 47.42 — `maximumAge: 0` forces a fresh reading each
                // interval (was 30 s cache → stale locations for moving staff).
                // High-accuracy uses the GPS antenna directly; timeout 20 s.
                { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
            );
        });
    };

    // Phase 47.42 — Also open a `watchPosition` stream in parallel so any
    // upgraded fix (GPS lock, better satellite view, moving vehicle) is
    // captured live rather than waiting for the next 60 s tick. The watch
    // callback shares the same "reject > 500 m" gate so IP fallbacks
    // never sneak in.
    const watchIdRef = useRef(null);
    useEffect(() => {
        if (!enabled || !canTrack || !supported) return;
        let last = 0;
        watchIdRef.current = navigator.geolocation.watchPosition(
            async (pos) => {
                const now = Date.now();
                // Throttle watch-posted samples to 30 s so we don't hammer
                // the API in dense-sample environments (e.g. bus rides).
                if (now - last < 30000) return;
                const acc = pos.coords.accuracy || 9999;
                if (acc > 500) return;
                last = now;
                try {
                    const battery = await getBatteryLevel();
                    const res = await api.reportStaffLocation({
                        latitude: pos.coords.latitude,
                        longitude: pos.coords.longitude,
                        accuracy: acc,
                        heading: pos.coords.heading,
                        speed: pos.coords.speed,
                        battery,
                        source: 'live-watch',
                    });
                    setLastReport(res.updated_at || new Date().toISOString());
                    setLastError(null);
                    setPermission('granted');
                } catch (e) {
                    // Non-fatal — periodic timer will retry
                }
            },
            (err) => {
                setPermission(err?.code === 1 ? 'denied' : 'error');
            },
            { enableHighAccuracy: true, maximumAge: 0, timeout: 30000 }
        );
        return () => {
            if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, canTrack, supported]);

    useEffect(() => {
        clearInterval(timerRef.current);
        if (!enabled || !canTrack || !supported) return;
        // Fire once immediately, then on interval
        reportOnce();
        timerRef.current = setInterval(reportOnce, intervalMs);
        return () => clearInterval(timerRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, canTrack, supported, intervalMs]);

    return { enabled, setEnabled, lastReport, lastError, supported, permission, canTrack, reportOnce };
}

async function getBatteryLevel() {
    try {
        if (navigator.getBattery) {
            const b = await navigator.getBattery();
            return Math.round(b.level * 100);
        }
    } catch (e) {
        // Battery API unavailable (Safari, some Android WebViews) — battery level stays null.
        // Not user-facing; do not toast.
    }
    return null;
}

export default useLocationSharing;
